import { useState } from "react";
import { motion } from "framer-motion";
import ParticleBackground from "../components/ParticleBackground";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  MessageCircle,
  User,
  Building2,
} from "lucide-react";

function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const sendWhatsApp = (e) => {
    e.preventDefault();

    const text = encodeURIComponent(
      `Hello Eiffeltech, I would like to make an enquiry.

Name: ${form.name}
Company: ${form.company || "Not provided"}
Phone: ${form.phone}
Email: ${form.email || "Not provided"}
Message: ${form.message}`
    );

    window.open(`https://wa.me/263716292923?text=${text}`, "_blank");
  };

  return (
    <section className="relative flex min-h-[88vh] items-center justify-center overflow-hidden px-4 pb-10 pt-24 sm:min-h-screen sm:px-6 sm:pt-28">

  {/* BACKGROUND */}
  <div className="absolute inset-0 z-0">
    <ParticleBackground />
  </div>

 

      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-4xl text-center"
        >
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-400 sm:text-sm sm:tracking-[0.3em]">
            Contact Eiffeltech
          </p>

          <h1 className="mt-4 text-3xl font-black leading-tight tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            Let’s build your next digital solution
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-300 sm:mt-6 sm:text-lg sm:leading-8">
            Reach out to Eiffeltech for websites, systems, platforms and smart
            digital solutions.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          <ContactCard
            icon={<Phone size={24} />}
            title="Phone / WhatsApp"
            link="tel:+263716292923"
            text="+263716292923"
          />

          <ContactCard
            icon={<Mail size={24} />}
            title="Email"
            link="mailto:info@eiffeltech.co.zw"
            text="info@eiffeltech.co.zw"
          />

          <ContactCard icon={<MapPin size={24} />} title="Location" text="Zimbabwe" />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-16 lg:grid-cols-2 lg:gap-8">
          <div className="rounded-[1.5rem] border border-cyan-400/20 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl sm:rounded-3xl sm:p-8">
            <h2 className="text-2xl font-black sm:text-3xl">
              Send us a message
            </h2>

            <div className="mt-5 space-y-3 sm:mt-6 sm:space-y-4">
              <InfoLine icon={<User size={20} />} label="CEO" value="Eiffeltech" />

              <InfoLine
                icon={<Phone size={20} />}
                label="Phone"
                value="+263716292923"
                link="tel:+263716292923"
              />

              <InfoLine
                icon={<Mail size={20} />}
                label="Email"
                value="info@eiffeltech.co.zw"
                link="mailto:info@eiffeltech.co.zw"
              />

              <InfoLine
                icon={<Building2 size={20} />}
                label="Company"
                value="Eiffeltech Digital Solutions"
              />
            </div>

            <a
              href="https://wa.me/263716292923"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-cyan-300 sm:mt-7 sm:w-auto sm:px-6 sm:py-4 sm:text-base"
            >
              <MessageCircle size={18} />
              Chat on WhatsApp
            </a>
          </div>

          <form
            onSubmit={sendWhatsApp}
            className="rounded-[1.5rem] border border-cyan-400/20 bg-slate-900/80 p-5 shadow-xl backdrop-blur-xl sm:rounded-3xl sm:p-8"
          >
            <div className="grid gap-3 sm:gap-4">
              <Input
                name="name"
                onChange={handleChange}
                required
                placeholder="Your full name"
              />

              <Input
                name="company"
                onChange={handleChange}
                placeholder="Company"
              />

              <Input
                name="phone"
                onChange={handleChange}
                required
                placeholder="Phone"
              />

              <Input
                name="email"
                type="email"
                onChange={handleChange}
                placeholder="Email"
              />

              <textarea
                name="message"
                onChange={handleChange}
                required
                rows="5"
                placeholder="Tell us what you need"
                className="min-h-32 w-full rounded-2xl border border-cyan-400/20 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 sm:text-base"
              />

              <button className="mt-1 flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-cyan-300 sm:py-4 sm:text-base">
                <Send size={18} /> Send Message
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Input({ ...props }) {
  return (
    <input
      {...props}
      className="w-full rounded-2xl border border-cyan-400/20 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 sm:text-base"
    />
  );
}

function ContactCard({ icon, title, text, link }) {
  const content = (
    <>
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-400 text-slate-950 sm:h-14 sm:w-14">
        {icon}
      </div>
      <h2 className="text-base font-black sm:text-lg">{title}</h2>
      <p className="mt-2 break-words text-sm text-slate-300">
        {text}
      </p>
    </>
  );

  return (
    <motion.div
      whileHover={{ y: -6 }}
      className="rounded-[1.5rem] border border-cyan-400/20 bg-slate-900/80 p-5 text-center shadow-xl backdrop-blur-xl sm:rounded-3xl sm:p-6"
    >
      {link ? (
        <a href={link} className="block hover:opacity-80">
          {content}
        </a>
      ) : (
        content
      )}
    </motion.div>
  );
}

function InfoLine({ icon, label, value, link }) {
  const content = (
    <>
      <div className="shrink-0 text-cyan-400">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-bold text-cyan-400 sm:text-sm">{label}</p>
        <p className="break-words text-sm text-white">
          {value}
        </p>
      </div>
    </>
  );

  return link ? (
    <a
      href={link}
      className="flex gap-3 rounded-2xl bg-cyan-400/10 p-4 hover:bg-cyan-400/20"
    >
      {content}
    </a>
  ) : (
    <div className="flex gap-3 rounded-2xl bg-cyan-400/10 p-4">
      {content}
    </div>
  );
}

export default Contact;
